import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../../App.css';
import './PromptManagementPage.css';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import { useAuth } from '../../contexts/AuthContext.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';

// タブの定義（maxPrompts: バックエンド側 prompt_templates.py の MAX_TENANT_PROMPTS_BY_TYPE と一致させること）
const PROMPT_TABS = [
    { id: 'summary', label: '要約', promptType: 'summary', maxPrompts: 10 },
    { id: 'recommendation', label: '推薦文', promptType: 'recommendation', maxPrompts: 10 },
    { id: 'feedback', label: 'フィードバック', promptType: 'feedback', maxPrompts: 3 },
];

function PromptManagementPage() {
    const [activeTab, setActiveTab] = useState('summary');
    const [systemTemplates, setSystemTemplates] = useState([]);
    const [tenantTemplates, setTenantTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, template: null });
    const [deleteLoading, setDeleteLoading] = useState(false);

    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // 管理者チェック
    useEffect(() => {
        if (currentUser && !currentUser.is_admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    // 現在のタブに対応するprompt_type
    const currentPromptType = PROMPT_TABS.find(tab => tab.id === activeTab)?.promptType || 'summary';

    // テンプレート一覧取得（並列）
    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [systemRes, tenantRes] = await Promise.all([
                api.fetch(`/prompts/system-templates?prompt_type=${currentPromptType}`),
                api.fetch(`/prompts/tenant-templates?prompt_type=${currentPromptType}`)
            ]);

            if (!systemRes || !tenantRes) return;

            if (systemRes.status === 403 || tenantRes.status === 403) {
                setError('管理者権限が必要です。');
                setLoading(false);
                setTimeout(() => navigate('/'), 2000);
                return;
            }

            if (!systemRes.ok) throw new Error('システムプロンプト一覧の取得に失敗しました。');
            if (!tenantRes.ok) throw new Error('カスタムプロンプト一覧の取得に失敗しました。');

            const systemData = await systemRes.json();
            const tenantData = await tenantRes.json();

            setSystemTemplates(systemData.templates || []);
            setTenantTemplates(tenantData.templates || []);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [navigate, currentPromptType]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates, activeTab]);

    // テナントプロンプトの件数（論理削除されていないもの）
    const currentTab = PROMPT_TABS.find(tab => tab.id === activeTab) || PROMPT_TABS[0];
    const tenantPromptCount = tenantTemplates.length;
    const canCreateNew = tenantPromptCount < currentTab.maxPrompts;

    // プロンプト削除
    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            const response = await api.fetch(`/prompts/templates/${deleteModal.template.id}`, {
                method: 'DELETE'
            });

            if (response && response.ok) {
                setDeleteModal({ open: false, template: null });
                fetchTemplates();
            } else {
                const errorMessage = await handleApiError(response, 'プロンプトの削除に失敗しました。');
                alert(errorMessage);
            }
        } catch (err) {
            alert('通信エラーが発生しました。');
        } finally {
            setDeleteLoading(false);
        }
    };

    // システムプロンプトの表示/非表示トグル
    const handleToggleSystemVisibility = async (e, template) => {
        e.stopPropagation();
        try {
            const response = await api.fetch(`/prompts/system-templates/${template.id}/visibility`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    is_hidden: !template.is_hidden
                })
            });

            if (response && response.ok) {
                fetchTemplates();
            } else {
                const errorMessage = await handleApiError(response, '表示設定の更新に失敗しました。');
                alert(errorMessage);
            }
        } catch (err) {
            alert('通信エラーが発生しました。');
        }
    };

    // 削除ボタンのクリックハンドラ
    const handleDeleteClick = (e, template) => {
        e.stopPropagation();
        setDeleteModal({ open: true, template });
    };

    // 行クリックで編集画面へ
    const handleRowClick = (template) => {
        navigate(`/settings/prompts/${template.id}/edit`);
    };

    // タブのラベル取得
    const currentTabLabel = PROMPT_TABS.find(tab => tab.id === activeTab)?.label || '要約';

    return (
        <div className="user-management-page">
            {/* ヘッダー */}
            <div className="prompt-page-header">
                <div>
                    <h1 className="page-title">プロンプト管理</h1>
                    <p className="prompt-page-description">
                        {currentTabLabel}生成に使用するプロンプトテンプレートを管理します
                    </p>
                </div>
            </div>

            {/* タブ */}
            <div className="prompt-tabs">
                {PROMPT_TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`prompt-tab ${activeTab === tab.id ? 'prompt-tab-active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {loading && <SkeletonLoader variant="list" rows={3} />}
            {error && <p className="error-message">エラー: {error}</p>}

            {!loading && !error && (
                <>
                    {/* システムプロンプト セクション */}
                    <div className="meeting-list-card">
                        <h2 className="card-title">システムプロンプト</h2>
                        <p className="prompt-section-description">
                            システム提供のプロンプトです。表示/非表示を切り替えられます。
                        </p>
                        <div className="prompt-list">
                            {systemTemplates.length > 0 ? (
                                systemTemplates.map(template => (
                                    <div key={template.id} className="prompt-list-item">
                                        <div className="prompt-item-left">
                                            <span className="prompt-badge prompt-badge-system">システム</span>
                                            <div className="prompt-item-info">
                                                <div className="prompt-item-name">{template.name}</div>
                                                <div className="prompt-item-description">
                                                    {template.description || 'プロンプトの説明はありません'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="prompt-item-right">
                                            <span
                                                onClick={(e) => handleToggleSystemVisibility(e, template)}
                                                className={`prompt-status-badge clickable ${template.is_hidden ? 'prompt-status-inactive' : 'prompt-status-active'}`}
                                                title="クリックで表示設定を変更"
                                            >
                                                {template.is_hidden ? '非表示' : '表示中'}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="prompt-empty-state">
                                    システムプロンプトがありません。
                                </div>
                            )}
                        </div>
                    </div>

                    {/* カスタムプロンプト セクション */}
                    <div className="meeting-list-card" style={{ marginTop: '20px' }}>
                        <div className="prompt-section-header">
                            <div>
                                <h2 className="card-title">カスタムプロンプト</h2>
                                <p className="prompt-section-description">
                                    テナント独自のプロンプトです。
                                </p>
                            </div>
                            <div className="prompt-header-actions">
                                <span className="prompt-count-label">
                                    {tenantPromptCount} / {currentTab.maxPrompts}件
                                </span>
                                <Link
                                    to={canCreateNew ? `/settings/prompts/new?type=${currentPromptType}` : "#"}
                                    className={`btn-create-prompt${!canCreateNew ? ' disabled' : ''}`}
                                >
                                    + 新規プロンプト
                                </Link>
                            </div>
                        </div>

                        {!canCreateNew && (
                            <div className="prompt-warning-banner">
                                ⚠️ プロンプトが上限（{currentTab.maxPrompts}件）に達しています。
                            </div>
                        )}

                        <div className="prompt-list">
                            {tenantTemplates.length > 0 ? (
                                tenantTemplates.map(template => (
                                    <div
                                        key={template.id}
                                        className="prompt-list-item prompt-list-item-clickable"
                                        onClick={() => handleRowClick(template)}
                                    >
                                        <div className="prompt-item-left">
                                            <span className="prompt-badge prompt-badge-custom">カスタム</span>
                                            <div className="prompt-item-info">
                                                <div className="prompt-item-name">{template.name}</div>
                                                <div className="prompt-item-description">
                                                    {template.description || 'プロンプトの説明はありません'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="prompt-item-right">
                                            <button
                                                onClick={(e) => handleDeleteClick(e, template)}
                                                className="btn-prompt-delete"
                                            >
                                                削除
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="prompt-empty-state">
                                    カスタムプロンプトがありません。「+ 新規プロンプト」から追加してください。
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* 削除確認モーダル */}
            {deleteModal.open && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">プロンプト削除の確認</h3>
                        <div className="prompt-delete-modal-text">
                            <p>「{deleteModal.template.name}」を削除しますか？</p>
                            <p className="prompt-delete-warning">この操作は取り消せません。</p>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="modal-button-cancel"
                                onClick={() => setDeleteModal({ open: false, template: null })}
                                disabled={deleteLoading}
                            >
                                キャンセル
                            </button>
                            <button
                                className="btn-delete"
                                onClick={handleDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? '削除中...' : '削除する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PromptManagementPage;
